import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import { Form } from 'semantic-ui-react'
import { Maybe } from '@adc/sdk/types'
import { FilterSchema, filters } from '@adc/blueprint'
import Text from '../../text'
import InputField from '../../../formFields/inputField'
import DatePickerField from '../../../formFields/datePickerField'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const BLUR_TIMEOUT = 250

interface FieldProps {
  type: 'Date' | 'input'
  label: string
  name: string
  placeholder?: string
  elmId: string
  value: string | number | Date | undefined
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: () => void
  hideWrapper: boolean
  autoComplete: string
  disabledDays?: {
    before?: Maybe<Date>
    after?: Maybe<Date>
  }
}

/**
 * Combo field to support both Date and input fields
 */
const Field: React.FC<FieldProps> = ({ type, ...props }) => {
  if (type === 'Date') {
    return <DatePickerField {...props} />
  } else {
    return <InputField {...props} />
  }
}

type RangeProps = FilterSchema.FilterLayoutProps<
  FilterSchema.FilterRangeDefinition,
  FilterSchema.RangeValueType<FilterSchema.RangeControlValueType>
>

const Range: React.FunctionComponent<RangeProps> = ({
  definition,
  value,
  elmId,
  onChange,
}) => {
  // keep track locally which values are selected
  const [currentValue, setCurrentValue] = useState(
    value || { min: null, max: null }
  )

  const currentValueRef = useRef(currentValue)

  useEffect(() => {
    currentValueRef.current = currentValue
  }, [currentValue])

  useEffect(() => {
    setCurrentValue(value)
  }, [value])

  const getValueFromEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (definition.controlValueType === 'number')
      return parseFloat(e.target.value) || null
    else if (definition.controlValueType === 'Date') {
      const newDate = new Date(e as unknown as string)
      return isNaN(newDate as unknown as number) ? null : newDate
    } else return e.target.value
  }

  const validate = (
    rangeValue: FilterSchema.RangeValueType<FilterSchema.RangeControlValueType>,
    minOrMax: 'min' | 'max'
  ) => {
    let newMin = rangeValue?.min || null
    let newMax = rangeValue?.max || null

    if (newMin !== null && newMax !== null && newMin > newMax) {
      if (minOrMax === 'min') newMin = null
      else newMax = null
    }
    return { min: newMin, max: newMax }
  }

  const onChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    minOrMax: 'min' | 'max'
  ) => {
    const val = getValueFromEvent(e)
    const newValue =
      minOrMax === 'min'
        ? {
            min: val,
            max: currentValue?.max,
          }
        : {
            min: currentValue?.min,
            max: val,
          }
    setCurrentValue(newValue)
  }

  const onBlurHandler = (minOrMax: 'min' | 'max') => {
    setTimeout(() => {
      const newValue = validate(currentValueRef.current, minOrMax)
      setCurrentValue(newValue)
      onChange(definition.name, newValue)
    }, BLUR_TIMEOUT)
  }

  const isDate = definition.controlValueType === 'Date'
  // DatePickerField requires string value e.g. '12/30/2024'
  const minValueFormatted = isDate
    ? filters.utils.formatDate(currentValue?.min as string)
    : currentValue?.min || ''

  const maxValueFormatted = isDate
    ? filters.utils.formatDate(currentValue?.max as string)
    : currentValue?.max || ''

  return (
    <Form className={cx('row-layout', 'range')}>
      <div
        className={cx('row-item', {
          half: definition.controlValueType !== 'Date',
        })}
      >
        {isDate && (
          <Text color='gray' size='tiny' className={cx('description')}>
            From
          </Text>
        )}
        <Field
          type={isDate ? 'Date' : 'input'}
          label=''
          name={definition.name}
          placeholder={isDate ? 'MM/DD/YYYY' : definition.placeholderMin}
          elmId={`${elmId}_${definition.name}_min`}
          value={minValueFormatted}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChangeHandler(e, 'min')
          }}
          onBlur={() => onBlurHandler('min')}
          hideWrapper
          autoComplete='off'
          {...(isDate
            ? {
                disabledDays: {
                  after: currentValue?.max as Date,
                },
              }
            : {})}
        />
      </div>
      <div className={cx('row-item', 'half')}>
        {isDate && (
          <Text color='gray' size='tiny' className={cx('description')}>
            To
          </Text>
        )}
        <Field
          type={isDate ? 'Date' : 'input'}
          label=''
          name={definition.name}
          placeholder={isDate ? 'MM/DD/YYYY' : definition.placeholderMax}
          elmId={`${elmId}_${definition.name}_max`}
          value={maxValueFormatted}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChangeHandler(e, 'max')
          }}
          onBlur={() => onBlurHandler('max')}
          hideWrapper
          autoComplete='off'
          {...(isDate
            ? {
                disabledDays: {
                  before: currentValue?.min as Date,
                },
              }
            : {})}
        />
      </div>
    </Form>
  )
}

export default Range
